import React, { useEffect, useState } from 'react'
import { Form } from 'react-router-dom';
import { Alert, Button, IconButton, MenuItem, Select } from '@mui/material';

import { ICustomerLight } from '../../types/customer';
import { IEmployeeTilsAccess } from '../../types/employee';

import { EmployeeService } from '../../services/employeeService';
import { CustomerService } from '../../services/customerService';
import { UtilityService } from '../../services/utilityService';

import Spinner from '../../components/spinner';
import Json from '../json';

import '../../styles/employeeTilsAccessForm.scss';
import { Icons } from '../icons';

export interface IEmployeeTilsAccessFormProps {
	id: string;
	tilsAccess: IEmployeeTilsAccess[];
}

const EmployeeTilsAccessForm: React.FunctionComponent<IEmployeeTilsAccessFormProps> = ({ id, tilsAccess }) => {
	const [allCustomers, setAllCustomers] = useState<ICustomerLight[]>([]);	
	const [assignedFacilityIds, setAssignedFacilityIds] = useState<string[]>([]);
	const [tilsAccessState, setTilsAccessState] = useState<IEmployeeTilsAccess[]>([...tilsAccess ?? []]);

	const [employeeCustomers, setEmployeeCustomers] = useState<ICustomerLight[]>([]);
	const [unassignedCustomers, setUnassignedCustomers] = useState<ICustomerLight[]>([]);
	const [customerIdField, setCustomerIdField] = useState("");
	const [selectedCustomer, setSelectedCustomer] = useState<ICustomerLight>();
	const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
	const [isGrantingAccess, setIsGrantingAccess] = useState(false);
	const [isRemovingAccess, setIsRemovingAccess] = useState(false);
	const [isRemovingCustomerId, setIsRemovingCustomerId] = useState("");
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	useEffect(() => {
		syncTilsAccessWithEmployee();
		const facilityIds = tilsAccessState.map((f) => f.customerId.toString());
		setAssignedFacilityIds(facilityIds);
		loadCustomers(facilityIds);
	}, [tilsAccessState])

	useEffect(() => {
		let customer = unassignedCustomers.find(c => c.id?.toString() == customerIdField);
		setSelectedCustomer(customer);
	}, [customerIdField])
	
	const loadCustomers = async (facilityIds: string[]) => {
		setIsLoadingCustomers(true);

		//If you don't have the customer list, get it, if you do, create a local working copy
		let customers: ICustomerLight[] = [];
		if(allCustomers.length < 1){
			let customerResponse = await CustomerService.getCustomers();
			customers = CustomerService.mapCustomerLightList(customerResponse.data);
			//Limit list to active customers
			customers = customers.filter((c => c.status == 'Active'));
		}
		else{
			customers = allCustomers;
		}

		let employeeHas: ICustomerLight[] = [];
		let employeeHasNot: ICustomerLight[] = [];

		customers.map((item) => {
			if (item.id && facilityIds.indexOf(item.id.toString()) >= 0) {
				employeeHas.push(item);
			}
			else {
				employeeHasNot.push(item);
			}
		});

		


		employeeHas.sort((a, b) => UtilityService.compareStrings(a.name, b.name))
		employeeHasNot.sort((a, b) => UtilityService.compareStrings(a.name, b.name))


		setEmployeeCustomers(employeeHas);
		setUnassignedCustomers(employeeHasNot);

		setCustomerIdField((employeeHasNot && employeeHasNot.length > 0 && employeeHasNot[0].id) ? employeeHasNot[0].id : "")
		//setSelectedCustomer(employeeHasNot ? employeeHasNot[0] : undefined);
		setIsLoadingCustomers(false);
	}

	const handleGrantAccess = async () => {
		setErrorMessage(undefined);
		setIsGrantingAccess(true);

			EmployeeService.grantTilsAccess(id, customerIdField)
			.then((response) => {
				console.log('EmployeeService.grantTilsAccess: SUCCESS', response);
				setTilsAccessState([...tilsAccessState, response.data]);
			})
			.catch((error) => {
				console.log('EmployeeService.grantTilsAccess: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
			})
			.finally(() => {
				setIsGrantingAccess(false);
			});
	}

	const handleRevokeAccess = async (customerId: string | undefined) => {
		setErrorMessage(undefined);

		if (customerId) {
			EmployeeService.revokeTilsAccess(id, customerId)
			.then((response) => {
				console.log('EmployeeService.revokeTilsAccess: SUCCESS', response);
				setTilsAccessState(tilsAccessState.filter((item) => item.customerId.toString() != customerId));
			})
			.catch((error) => {
				console.log('EmployeeService.revokeTilsAccess: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
			});
		}
	}

	// Sync the employee's referenced TILS access array passed in from the parent page with changes made here
	const syncTilsAccessWithEmployee = () => {
		tilsAccess.splice(0);
		tilsAccess.push(...tilsAccessState);
	}

	return (
		<section className="hwi-employee-tils-access-form">
			<Spinner dependency={isLoadingCustomers} text='Loading employee TILS...'>
				<div className="facility-table">
					<div className="headers">
						<div>Customer</div>
						<div>City</div>
						<div>State</div>
						<div></div>
					</div>
					<div className="body">
						{employeeCustomers.length > 0 ? (
							<>
								{employeeCustomers.map((item, index) => (
									<div className="row" key={index}>
										<div>{item.name} {item.status == "Inactive" ? '(Inactive)' : ''}</div>
										<div>{item.city}</div>
										<div>{item.state}</div>
										<div><Button onClick={() => handleRevokeAccess(item?.id)} disabled={isRemovingAccess}>{isRemovingAccess && isRemovingCustomerId == item.id ? "Removing..." : (<><Icons.RemoveItem fill="#a82525" height="16px"></Icons.RemoveItem><span>Remove</span></>)}</Button></div>
									</div>
								))}
							</>
						) : (
							<div className="no-results">0 results</div>
						)}

					</div>
				</div>
				{errorMessage &&
					<Alert className="alert-error" severity="error" action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>{errorMessage}</Alert>
				}
				<Form>
					<div className="row">
						<div>
							<Select
								required
								className="status-field"
								value={customerIdField}
								fullWidth
								onChange={(event) => setCustomerIdField(event.target.value)}
							>
								{unassignedCustomers.filter((c => c.status == 'Active')).map((item, index) => (
									<MenuItem value={item.id} key={index} >{item.name}</MenuItem>
								))}
							</Select>

						</div>
						<div>
							{selectedCustomer?.city}
						</div>
						<div>
							{selectedCustomer?.state}
						</div>
						<div className="controls">
							<Button variant="text" 
								className="save-button"
								disabled={isGrantingAccess}
								onClick={handleGrantAccess} >{isGrantingAccess ? "Saving..." : (
									<>
										<Icons.AddItem fill="#272f50" height="16px"></Icons.AddItem>
										<span> Grant Access</span>
									</>
								)}</Button>
						</div>
					</div>
				</Form>
			</Spinner>
			{/* <Json data={customerIdField}></Json> */}
			{/* <Json data={assignedFacilityIds}></Json> */}
			{/* <Json data={employeeCustomers}></Json> */}
		</section>
	)
}

export default EmployeeTilsAccessForm;